'use client';

import React, { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

// Backend integration point: GET /api/ehs/moc?page=1&limit=10
const mocItems = [
  {
    id: 'MOC-2026-041',
    title: 'Replace cooling tower water treatment chemical',
    initiator: 'Priya Nair',
    department: 'Chemical Proc.',
    site: 'Houston TX',
    riskLevel: 'high' as const,
    status: 'pending' as const,
    submitted: '04/20/2026',
    dueDate: '04/30/2026',
    approver: 'Marcus Reyes',
    overdue: false,
  },
  {
    id: 'MOC-2026-040',
    title: 'Modify pump seal configuration in Unit 4',
    initiator: 'James Okafor',
    department: 'Operations',
    site: 'Corpus Christi TX',
    riskLevel: 'medium' as const,
    status: 'in-progress' as const,
    submitted: '04/18/2026',
    dueDate: '04/28/2026',
    approver: 'Elena Vasquez',
    overdue: true,
  },
  {
    id: 'MOC-2026-039',
    title: 'Install new ventilation system in Lab B',
    initiator: 'Mei-Lin Chen',
    department: 'Chemical Proc.',
    site: 'Denver CO',
    riskLevel: 'medium' as const,
    status: 'approved' as const,
    submitted: '04/10/2026',
    dueDate: '04/25/2026',
    approver: 'Marcus Reyes',
    overdue: false,
  },
  {
    id: 'MOC-2026-038',
    title: 'Update emergency shutdown procedure ESD-12',
    initiator: 'Darnell Washington',
    department: 'Operations',
    site: 'Baton Rouge LA',
    riskLevel: 'high' as const,
    status: 'pending' as const,
    submitted: '04/15/2026',
    dueDate: '04/29/2026',
    approver: 'Tobias Müller',
    overdue: false,
  },
  {
    id: 'MOC-2026-037',
    title: 'Introduce new solvent in painting process',
    initiator: 'Fatima Al-Rashid',
    department: 'Maintenance',
    site: 'Houston TX',
    riskLevel: 'critical' as const,
    status: 'in-progress' as const,
    submitted: '04/08/2026',
    dueDate: '04/22/2026',
    approver: 'Marcus Reyes',
    overdue: true,
  },
  {
    id: 'MOC-2026-036',
    title: 'Change electrical panel layout — Building 3',
    initiator: 'Samuel Osei',
    department: 'Electrical',
    site: 'Baton Rouge LA',
    riskLevel: 'medium' as const,
    status: 'approved' as const,
    submitted: '04/05/2026',
    dueDate: '04/20/2026',
    approver: 'Ingrid Svensson',
    overdue: false,
  },
  {
    id: 'MOC-2026-035',
    title: 'Revise confined space entry permit SOP',
    initiator: 'Ananya Krishnan',
    department: 'Maintenance',
    site: 'Corpus Christi TX',
    riskLevel: 'high' as const,
    status: 'draft' as const,
    submitted: '04/01/2026',
    dueDate: '05/01/2026',
    approver: 'Pending assignment',
    overdue: false,
  },
  {
    id: 'MOC-2026-034',
    title: 'Upgrade fire suppression system in Warehouse A',
    initiator: 'David Okonkwo',
    department: 'Construction',
    site: 'Houston TX',
    riskLevel: 'high' as const,
    status: 'approved' as const,
    submitted: '03/28/2026',
    dueDate: '04/15/2026',
    approver: 'Marcus Reyes',
    overdue: false,
  },
  {
    id: 'MOC-2026-033',
    title: 'Implement new PPE standard for chemical handling',
    initiator: 'Marcus Reyes',
    department: 'Chemical Proc.',
    site: 'All Sites',
    riskLevel: 'medium' as const,
    status: 'pending' as const,
    submitted: '03/25/2026',
    dueDate: '04/10/2026',
    approver: 'Board Review',
    overdue: true,
  },
  {
    id: 'MOC-2026-032',
    title: 'Relocate chemical storage — Sector 7',
    initiator: 'Priya Nair',
    department: 'Chemical Proc.',
    site: 'Houston TX',
    riskLevel: 'critical' as const,
    status: 'in-progress' as const,
    submitted: '03/20/2026',
    dueDate: '04/05/2026',
    approver: 'Marcus Reyes',
    overdue: true,
  },
];

type SortKey = keyof typeof mocItems[0];

export default function MOCPipelineTable() {
  const [sortKey, setSortKey] = useState<SortKey>('submitted');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const sorted = [...mocItems].sort((a, b) => {
    const av = a[sortKey] as string | boolean;
    const bv = b[sortKey] as string | boolean;
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={11} className="opacity-40" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={11} className="text-[hsl(var(--primary))]" />
    ) : (
      <ChevronDown size={11} className="text-[hsl(var(--primary))]" />
    );
  };

  const columns: { key: SortKey; label: string }[] = [
    { key: 'id', label: 'MOC ID' },
    { key: 'title', label: 'Change Description' },
    { key: 'initiator', label: 'Initiator' },
    { key: 'department', label: 'Department' },
    { key: 'site', label: 'Site' },
    { key: 'riskLevel', label: 'Risk Level' },
    { key: 'status', label: 'Status' },
    { key: 'dueDate', label: 'Due Date' },
  ];

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">MOC Pipeline</h3>
          <span className="text-[11px] font-600 px-2 py-0.5 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] tabular-nums">
            {mocItems.length} active
          </span>
          <span className="text-[11px] font-600 px-2 py-0.5 rounded-md bg-red-500/10 text-red-500 tabular-nums">
            {mocItems.filter((m) => m.overdue).length} overdue
          </span>
        </div>
        <button
          className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1"
          onClick={() => toast.info('MOC creation form coming soon')}
        >
          + New MOC
        </button>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)]">
              {columns.map((col) => (
                <th
                  key={`moc-th-${col.key}`}
                  className="px-4 py-3 text-left text-[11px] font-600 uppercase tracking-wider text-[hsl(var(--muted-foreground))] cursor-pointer hover:text-[hsl(var(--foreground))] select-none"
                  onClick={() => toggleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-right text-[11px] font-600 uppercase tracking-wider text-[hsl(var(--muted-foreground))] w-28">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, idx) => (
              <tr
                key={row.id}
                className={`
                  border-b border-[hsl(var(--border))] last:border-0 transition-colors duration-100
                  hover:bg-[hsl(var(--muted)/0.5)]
                  ${idx % 2 === 0 ? '' : 'bg-[hsl(var(--muted)/0.15)]'}
                  ${row.overdue ? 'border-l-2 border-l-red-500' : ''}
                `}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {row.overdue && <Clock size={11} className="text-red-500 shrink-0" />}
                    <span className="font-mono text-xs font-500 text-[hsl(var(--foreground))] tabular-nums">
                      {row.id}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[220px]">
                  <p className="text-xs text-[hsl(var(--foreground))] truncate" title={row.title}>
                    {row.title}
                  </p>
                </td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--foreground))]">{row.initiator}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--foreground))]">{row.department}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--foreground))]">{row.site}</td>
                <td className="px-4 py-3">
                  <StatusBadge
                    variant={row.riskLevel}
                    label={row.riskLevel.charAt(0).toUpperCase() + row.riskLevel.slice(1)}
                  />
                </td>
                <td className="px-4 py-3">
                  <StatusBadge
                    variant={row.status}
                    label={
                      row.status === 'in-progress' ?'In Review'
                        : row.status.charAt(0).toUpperCase() + row.status.slice(1)
                    }
                  />
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs font-mono tabular-nums ${
                      row.overdue ? 'text-red-500 font-600' : 'text-[hsl(var(--muted-foreground))]'
                    }`}
                  >
                    {row.dueDate}
                    {row.overdue && ' ⚠'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      title="View MOC details"
                      className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all duration-150"
                    >
                      <Eye size={13} />
                    </button>
                    {row.status === 'pending' && (
                      <>
                        <button
                          title="Approve this MOC"
                          onClick={() => {
                            // TODO: Backend integration — PATCH /api/ehs/moc/:id/approve
                            toast.success(`MOC ${row.id} approved`, {
                              description: `Notifying ${row.initiator}`,
                            });
                          }}
                          className="p-1.5 rounded-md hover:bg-emerald-500/10 text-[hsl(var(--muted-foreground))] hover:text-emerald-500 transition-all duration-150"
                        >
                          <CheckCircle2 size={13} />
                        </button>
                        <button
                          title="Reject this MOC"
                          onClick={() => {
                            // TODO: Backend integration — PATCH /api/ehs/moc/:id/reject
                            toast.error(`MOC ${row.id} rejected`, {
                              description: 'Initiator will be notified with required changes.',
                            });
                          }}
                          className="p-1.5 rounded-md hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-all duration-150"
                        >
                          <XCircle size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
        <span className="text-xs text-[hsl(var(--muted-foreground))] tabular-nums">
          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, mocItems.length)} of {mocItems.length} MOC items
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed text-[hsl(var(--muted-foreground))] transition-all"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`moc-page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 rounded-md text-xs font-600 tabular-nums transition-all ${
                page === i + 1
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                  : 'hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed text-[hsl(var(--muted-foreground))] transition-all"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}